/**
 * responseCache — tiny in-memory cache for GET responses.
 *
 * Purpose: stop read-heavy endpoints (Professionals, Plans, Admin
 * dashboard) from re-fetching on every component mount. `useFetch` is
 * per-instance only; `dedupedGet` in apiClient covers concurrent
 * duplicates but not sequential ones across navigations.
 *
 * Contract:
 *   - Service functions (e.g. `fetchAdminDashboard`) decide IF + HOW
 *     LONG to cache. Consumers stay unchanged — they call the service
 *     function; the cache is transparent.
 *   - Mutations that invalidate a read use `cacheInvalidate('prefix:')`.
 *   - Keys are hierarchical (`'admin:dashboard'`, `'plans:list'`,
 *     `'networking:professionals:search=foo&page=2'`) so invalidation
 *     can target a family with one prefix.
 *
 * Non-goals:
 *   - Not a replacement for SWR/TanStack Query. No stale-while-revalidate
 *     revalidation loop, no focus/reconnect refresh, no dedup across
 *     hook instances within a single tick (that's `dedupedGet`'s job).
 *   - No persistence. Lives in module state; cleared on full reload.
 */

type Entry<T> = {
    data: T;
    expiresAt: number;
};

const store = new Map<string, Entry<unknown>>();

/** Returns the cached value if present AND not expired; otherwise null.
 *  Expired entries are deleted on read so the store stays bounded. */
export function cacheGet<T>(key: string): T | null {
    const entry = store.get(key);
    if (!entry) return null;
    if (Date.now() >= entry.expiresAt) {
        store.delete(key);
        return null;
    }
    return entry.data as T;
}

/** Write `data` under `key` with a TTL. Overwrites any existing entry. */
export function cacheSet<T>(key: string, data: T, ttlMs: number): void {
    store.set(key, { data, expiresAt: Date.now() + ttlMs });
}

/** Invalidate one key or every key starting with a prefix.
 *  Prefix form is the common case: `cacheInvalidate('networking:')`
 *  drops all networking reads after an accept/decline mutation. */
export function cacheInvalidate(keyOrPrefix: string): void {
    if (store.has(keyOrPrefix)) {
        store.delete(keyOrPrefix);
        return;
    }
    for (const key of store.keys()) {
        if (key.startsWith(keyOrPrefix)) store.delete(key);
    }
}

/** Test seam + logout hook — wipes the entire cache. */
export function cacheClear(): void {
    store.clear();
}

/** Build a stable key suffix from an object of query params. Same
 *  URLSearchParams sort-and-stringify pattern used by `dedupedGet`'s
 *  `dedupeKey` so a cache hit and an inflight-dedupe match the same
 *  request shape. */
export function cacheKeyFromParams(params: Record<string, unknown> | undefined): string {
    if (!params) return '';
    const sp = new URLSearchParams();
    Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([k, v]) => sp.append(k, String(v)));
    return sp.toString();
}

/** Read-through helper — returns cached value or calls `fetcher` once,
 *  writes the result to cache, and returns it. */
export async function cacheFetch<T>(
    key: string,
    ttlMs: number,
    fetcher: () => Promise<T>,
): Promise<T> {
    const cached = cacheGet<T>(key);
    if (cached !== null) return cached;
    const data = await fetcher();
    cacheSet(key, data, ttlMs);
    return data;
}
